/**
 * SizingDisplayObject — manages sizing visualization using a strategy pattern.
 *
 * Delegates rendering to view-mode-specific strategies:
 * - 3D: SizingRenderer3D (keypoint spheres, contour tubes, selected measurement)
 * - 2D Oblique: SizingRenderer2DSlice (contour polygon, diameter line)
 * - Other modes: no rendering
 */

import vtkActor from '@kitware/vtk.js/Rendering/Core/Actor';

import type { IViewport } from '../../viewports';
import type {
    IDisplayFeature,
    IDisplayObject,
    SizingDisplayData,
    SizingDisplayProperties,
    ViewMode,
} from '../types';
import { DisplayFeatureType, ViewMode as VM } from '../types';
import { SizingRenderer2DSlice } from './sizing-renderer-2d-slice';
import { SizingRenderer3D } from './sizing-renderer-3d';
import { SizingRendererCPR } from './sizing-renderer-cpr';

// ============================================================================
// Strategy interface
// ============================================================================

export interface ISizingRenderer {
    initialize(viewport: IViewport, featureId: string): void;
    update(data: SizingDisplayData, properties: SizingDisplayProperties): void;
    dispose(): void;
    getActors(): vtkActor[];
}

// ============================================================================
// Factory
// ============================================================================

function createRenderer(viewMode: ViewMode): ISizingRenderer | null {
    switch (viewMode) {
        case VM.VIEW_3D:
            return new SizingRenderer3D();
        case VM.VIEW_2D_OBLIQUE:
            return new SizingRenderer2DSlice();
        case VM.VIEW_CPR_STRETCHED:
        case VM.VIEW_CPR_STRAIGHTENED:
            return new SizingRendererCPR();
        default:
            return null;
    }
}

// ============================================================================
// Display Object
// ============================================================================

export class SizingDisplayObject implements IDisplayObject {
    readonly feature: IDisplayFeature<DisplayFeatureType.SIZING>;
    readonly viewMode: ViewMode;

    private renderer: ISizingRenderer | null;

    constructor(feature: IDisplayFeature<DisplayFeatureType.SIZING>, viewMode: ViewMode) {
        this.feature = feature;
        this.viewMode = viewMode;
        this.renderer = createRenderer(viewMode);
    }

    initialize(viewport: IViewport): void {
        this.renderer?.initialize(viewport, this.feature.id);
        this.applyUpdate();
    }

    update(): void {
        this.applyUpdate();
    }

    dispose(): void {
        this.renderer?.dispose();
    }

    getActors(): vtkActor[] {
        return this.renderer?.getActors() ?? [];
    }

    private applyUpdate(): void {
        if (!this.renderer) return;
        const data: SizingDisplayData = this.feature.data;
        if (!data) return;
        this.renderer.update(data, this.feature.properties);
    }
}
